import { useEffect, useMemo, useState } from 'react';
import { ImageOff } from 'lucide-react';
import { HoldsOverlay } from '../../../components/climbs/HoldsOverlay';
import { RouteLineOverlay } from '../../../components/climbs/RouteLineOverlay';
import { isDrawableLine } from '../../../components/climbs/route-line';
import { cropBackgroundStyle, focusCrop, startHoldsBounds } from '../../../components/climbs/hold-focus';
import type { Hold, RouteLine } from '../../../types/climb';

interface ClimbThumbnailProps {
  imageUrl: string | null;
  holds: Hold[] | null;
  /**
   * A route's path across the photo, drawn instead of circling its holds; null
   * on a boulder.
   */
  line: RouteLine | null;
  alt: string;
  onClick: () => void;
  label: string;
}

/** Square thumbnail for a boulder, so its crop is squared up to match. */
const CONTAINER_ASPECT = 1;

/**
 * The climb's photo, read the way its kind is read.
 *
 * A boulder is a handful of circled holds shot close up: what tells one from
 * the next on a crowded wall is where it starts, so the photo is cropped to its
 * start holds. A route is a line up the whole wall — cropping it anywhere would
 * throw away the shape that identifies it, so the photo is shown whole with the
 * line drawn on it and the wall around the corridor drained of colour. That
 * leaves the route as the only coloured band in the picture, which reads even
 * at this size.
 *
 * The box follows: square for a boulder's crop, portrait for a route, whose
 * photo is taken from the ground and would otherwise sit in a square frame with
 * bars either side.
 *
 * The natural dimensions come from a probe `Image` rather than the rendered
 * element: the crop is applied as a background, which has no `onLoad` to hang
 * the measurement off, and the aspect correction needs the real pixel shape.
 */
export function ClimbThumbnail({
  imageUrl,
  holds,
  line,
  alt,
  onClick,
  label,
}: ClimbThumbnailProps) {
  const [natural, setNatural] = useState<{ width: number; height: number } | null>(null);
  // Only a line with something to draw changes the reading; a route whose line
  // was never finished falls back to the boulder treatment, which for a climb
  // with no holds either is a plain uncropped photo.
  const drawnAsLine = isDrawableLine(line);

  useEffect(() => {
    if (!imageUrl) {
      setNatural(null);
      return;
    }

    let active = true;
    const probe = new Image();
    probe.onload = () => {
      if (active) setNatural({ width: probe.naturalWidth, height: probe.naturalHeight });
    };
    probe.src = imageUrl;

    return () => {
      active = false;
    };
  }, [imageUrl]);

  const crop = useMemo(() => {
    if (drawnAsLine || !natural) return null;

    const bounds = startHoldsBounds(holds);
    if (!bounds) return null;

    return focusCrop(bounds, natural.width, natural.height, CONTAINER_ASPECT);
  }, [drawnAsLine, holds, natural]);

  // Small enough that the card's own text, not the photo, sets its height —
  // a column shows more climbs that way, and the full photo is a click off.
  const boxSize = drawnAsLine ? 'h-16 w-11' : 'size-16';

  if (!imageUrl) {
    return (
      <div
        className={`flex ${boxSize} shrink-0 items-center justify-center rounded-md border bg-muted`}
        data-testid="climb-thumbnail-empty"
      >
        <ImageOff className="size-5 text-muted-foreground" aria-hidden />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      // The card itself is a drag handle; the image is a button, so keep the
      // press here from starting a drag.
      onPointerDown={(event) => event.stopPropagation()}
      aria-label={label}
      title={label}
      data-testid="climb-thumbnail"
      className={`relative ${boxSize} shrink-0 cursor-zoom-in overflow-hidden rounded-md border bg-muted bg-center bg-no-repeat transition-opacity hover:opacity-90`}
      style={{
        backgroundImage: `url(${imageUrl})`,
        // `contain` is what "the whole photo" means here: the line runs the
        // height of the wall, and covering the box would cut its ends off.
        ...(drawnAsLine ? { backgroundSize: 'contain' } : cropBackgroundStyle(crop)),
      }}
    >
      <span className="sr-only">{alt}</span>
      {/* The same marks the climb profile draws. Each renders nothing for the
          kind of climb it does not describe, so both can hang here. No
          animation: these are thumbnails, several to a column. */}
      <HoldsOverlay
        holds={holds}
        naturalWidth={natural?.width ?? null}
        naturalHeight={natural?.height ?? null}
        viewBox={
          crop && natural
            ? {
                x: crop.x * natural.width,
                y: crop.y * natural.height,
                width: crop.width * natural.width,
                height: crop.height * natural.height,
              }
            : null
        }
        objectFit="cover"
        mode="all"
        animate={false}
      />
      <RouteLineOverlay
        line={line}
        naturalWidth={natural?.width ?? null}
        naturalHeight={natural?.height ?? null}
        // Matches the contained background, so the line and the greyed-out wall
        // land on the photo rather than on the box around it.
        objectFit="contain"
        // At this size the full-size stroke resolves to a fraction of a pixel.
        marks="compact"
        animate={false}
      />
    </button>
  );
}
